import { parse } from '../interpolation';
import type { Expression } from '../interpolation/types';

export type ParamPath = ReadonlyArray<string | number>;

function readsEvent(expression: Expression): boolean {
  if (expression.kind === 'functionCall') {
    return expression.arg === undefined || readsEvent(expression.arg);
  }
  const root = expression.segments[0];
  return root !== undefined && root.type === 'key' && root.name === 'event';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Paths, inside an action's `params`, of every string that reads the `event`
 * root, which carries the payload a partner module emitted. A template that
 * does not parse counts as a read, so a shape this cannot analyse is refused
 * instead of passed.
 */
export function eventReferencePaths(
  params: unknown,
  path: ParamPath = [],
): ParamPath[] {
  if (typeof params === 'string') {
    if (!params.includes('{{')) return [];
    try {
      const segments = parse(params).segments;
      return segments.some(
        (segment) => segment.type === 'expression' && readsEvent(segment),
      )
        ? [path]
        : [];
    } catch {
      return [path];
    }
  }
  if (Array.isArray(params)) {
    return params.flatMap((entry, index) =>
      eventReferencePaths(entry, [...path, index]),
    );
  }
  if (isPlainObject(params)) {
    return Object.entries(params).flatMap(([name, entry]) =>
      eventReferencePaths(entry, [...path, name]),
    );
  }
  return [];
}
